import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Dino from 'react-chrome-dino';

const PageStyles = styled.div`
  min-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10rem 2rem;
`;

const Card = styled.div`
  text-align: center;
  padding: 5rem 6rem;
  border: 1px solid var(--gray-2);
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.35);
  max-width: 860px;
  width: 100%;
  background: var(--deep-dark);
  animation: fadeIn 0.6s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-16px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @media (max-width: 768px) {
    padding: 4rem 2.5rem;
  }
`;

const Heading = styled.h1`
  font-size: 5rem;
  font-family: 'Montserrat Bold';
  color: var(--highlight-blue);
  margin-bottom: 1rem;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 3.8rem;
  }
`;

const Sub = styled.h2`
  font-size: 2.2rem;
  color: var(--gray-1);
  opacity: 0.7;
  font-weight: normal;
  margin-bottom: 0.6rem;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const Body = styled.p`
  font-size: 1.5rem;
  color: var(--gray-1);
  opacity: 0.55;
  margin-bottom: 0.4rem;
  line-height: 1.6;
`;

const GameCard = styled.div`
  margin: 3rem 0 2rem;
  background: #f7f7f7;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid var(--gray-2);

  /* react-chrome-dino renders an iframe — force it to fill the container */
  iframe, canvas {
    display: block;
    width: 100% !important;
    border: none;
  }
`;

const ScoreBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3rem;
  margin-bottom: 2rem;

  .score-item {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }
  .score-label {
    font-size: 1.1rem;
    text-transform: uppercase;
    letter-spacing: 0.1rem;
    color: var(--gray-1);
    opacity: 0.5;
  }
  .score-value {
    font-size: 2.4rem;
    font-family: 'Montserrat Bold';
    color: var(--highlight-blue);
  }
`;

const GameOverBanner = styled.div`
  background: var(--gray-2);
  border: 1px solid var(--highlight-blue);
  border-radius: 10px;
  padding: 2rem 3rem;
  margin-bottom: 2rem;

  h3 {
    font-size: 2rem;
    color: var(--highlight-blue);
    margin-bottom: 0.5rem;
  }
  p {
    font-size: 1.4rem;
    color: var(--gray-1);
    opacity: 0.7;
    margin-bottom: 1.2rem;
  }
`;

const InstructionsBox = styled.div`
  background: var(--gray-2);
  border-left: 3px solid var(--highlight-blue);
  border-radius: 0 10px 10px 0;
  padding: 2rem 2.5rem;
  margin-bottom: 2rem;
  text-align: left;

  h4 {
    font-size: 1.6rem;
    color: var(--highlight-blue);
    margin-bottom: 1rem;
  }
  ul {
    list-style: none;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }
  li {
    font-size: 1.4rem;
    color: var(--gray-1);
    opacity: 0.8;
    line-height: 1.5;
    &::before {
      content: '→ ';
      color: var(--highlight-blue);
    }
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.2rem;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

const BtnPrimary = styled(Link)`
  font-size: 1.5rem;
  padding: 0.8em 2.2em;
  background: var(--highlight-blue);
  color: var(--dark-bg);
  border-radius: 8px;
  border: 2px solid var(--highlight-blue);
  cursor: pointer;
  transition: opacity 0.2s ease;
  font-family: 'RobotoMono Regular';
  &:hover { opacity: 0.85; }
`;

const BtnGhost = styled.button`
  font-size: 1.5rem;
  padding: 0.8em 2.2em;
  background: transparent;
  color: var(--highlight-blue);
  border-radius: 8px;
  border: 2px solid var(--highlight-blue);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  font-family: 'RobotoMono Regular';
  &:hover {
    background: var(--highlight-blue);
    color: var(--dark-bg);
  }
`;

export default function PageNotFound() {
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => Number(localStorage.getItem('highScore') || 0));
  const [isGameOver, setIsGameOver] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  const handleGameOver = (finalScore) => {
    setScore(finalScore);
    setIsGameOver(true);
    if (finalScore > highScore) {
      setHighScore(finalScore);
      localStorage.setItem('highScore', String(finalScore));
    }
  };

  const resetGame = () => {
    setIsGameOver(false);
    setScore(0);
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.keyCode === 32 || e.keyCode === 38) e.preventDefault();
    };
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []);

  return (
    <PageStyles>
      <Helmet><title>404 | Anas Qazi</title></Helmet>
      <Card>
        <Heading>404</Heading>
        <Sub>Page not found</Sub>
        <Body>The page you&apos;re looking for doesn&apos;t exist or has been moved.</Body>
        <Body>While you&apos;re here — try not to hit the cacti.</Body>

        {highScore > 0 && (
          <ScoreBar>
            <div className="score-item">
              <span className="score-label">Current</span>
              <span className="score-value">{score}</span>
            </div>
            <div className="score-item">
              <span className="score-label">Best</span>
              <span className="score-value">{highScore}</span>
            </div>
          </ScoreBar>
        )}

        <GameCard>
          <Dino onGameOver={handleGameOver} setScore={setScore} />
        </GameCard>

        {isGameOver && (
          <GameOverBanner>
            <h3>Game Over</h3>
            <p>Score: {score} · Best: {highScore}</p>
            <BtnGhost onClick={resetGame}>Play Again</BtnGhost>
          </GameOverBanner>
        )}

        {showInstructions && (
          <InstructionsBox>
            <h4>How to Play</h4>
            <ul>
              <li>Press <strong>Space</strong> or <strong>↑</strong> to jump</li>
              <li>Avoid the cacti and pterodactyls</li>
              <li>The game speeds up the longer you survive</li>
            </ul>
          </InstructionsBox>
        )}

        <Actions>
          <BtnPrimary to="/">Go Home</BtnPrimary>
          <BtnGhost onClick={() => setShowInstructions((v) => !v)}>
            {showInstructions ? 'Hide Instructions' : 'How to Play'}
          </BtnGhost>
        </Actions>
      </Card>
    </PageStyles>
  );
}
